// @ts-check
/**
 * A function to compare the closeness of two dates
 * @param {Date} date1 the first name
 * @param {Date} date2 the second name
 * @returns {[boolean, number, [number, number, number][] ]} a 3-tuple
 *  1. Whether or not it is a match
 *  2. Kind of match or reason why it wasn't-- 1 is a simple string match, 2 is a pronounciation match, 0 is a weak match, -1 is not a match, -2 is too few words
 *  3. Each member of the array is an array representing a token. First member is the index of the token in name 1, second member is the index of the token in name 2, third member is a score from 0-100 of how close of a match it was
 */
export default function compareTwoDates(date1, date2) {
  const date1Day = date1.getUTCDate();
  const date1Month = date1.getUTCMonth();
  const date1Year = date1.getUTCFullYear();
  const date2Day = date2.getUTCDate();
  const date2Month = date2.getUTCMonth();
  const date2Year = date2.getUTCFullYear();

  const dayMax = Math.max(daysInMonth(date1Month), daysInMonth(date2Month));
  const DAY_DIFF_MAX = 16;
  const dayDiff = getDifferenceWithWrapAround(date1Day, date2Day, 1, dayMax);
  const dayScore = 1 - dayDiff / DAY_DIFF_MAX; // 100% for a perfect match, 0% for furthest possible

  const MONTH_DIFF_MAX = 6;
  const monthDiff = getDifferenceWithWrapAround(date1Month, date2Month, 11);
  const monthScore = 1 - monthDiff / MONTH_DIFF_MAX;

  const YEAR_DIFF_MAX = 2;
  const yearDiff = Math.abs(date1Year - date2Year);
  let yearScore = 1 - yearDiff / YEAR_DIFF_MAX;
  if (yearScore < 0) {
    yearScore = 0;
  }

  // 3:2:1 weighting system
  const YEAR_WEIGHT = 50;
  const MONTH_WEIGHT = 33;
  const DAY_WEIGHT = 17;

  const weightedScore =
    yearScore * YEAR_WEIGHT + monthScore * MONTH_WEIGHT + dayScore * DAY_WEIGHT;
  const isMatch = weightedScore > 80;
  const matchType =
    /* if */ weightedScore === 1
      ? 1
      : /* else if */ weightedScore > 90
      ? 2
      : /* else if */ weightedScore > 80
      ? 0
      : /* else */ -1;
  const /** @type {[number, number, number][]} */ matchDetails = [
      [0, 0, yearScore * 100],
      [1, 1, monthScore * 100],
      [2, 2, dayScore * 100],
    ];
  return [isMatch, matchType, matchDetails];
}

/**
 * Returns the distance between two numbers.
 * Takes into account wraparounds so the distance between month 1 and month 12 is 1
 * @param {number} num1
 * @param {number} num2
 * @param {[number] | [number, number]} limits
 *  If one number is passed in, it will be treated as a max and default to [0, max].
 *  If two numbers are passed in, they will be treated as [min, max]
 * @returns {number} the distance between the numbers on a cyclical number line
 */
function getDifferenceWithWrapAround(num1, num2, ...limits) {
  const [min, max] = limits.length === 2 ? limits : [0, limits[0]];
  const range = max - min;

  let smallerNum, biggerNum;
  if (num1 < num2) {
    smallerNum = num1;
    biggerNum = num2;
  } else {
    smallerNum = num2;
    biggerNum = num1;
  }

  const directDiff = biggerNum - smallerNum;
  const wrapAroundDiff = smallerNum - (biggerNum - range);

  return Math.min(directDiff, wrapAroundDiff);
}

/**
 * Gets the number of days in a month
 * @param {number} month the month, 0-indexed (0-11)
 */
function daysInMonth(month) {
  return new Date(new Date().getFullYear(), month, 0).getDate();
}
